import axios, { AxiosInstance } from "axios";
import {
  AxiosRequestConfig,
  AxiosResponse,
  AxiosError,
} from "axios";

/* Configs */
import { ApiConfig } from "../config/Api.config";

/**
 * @class Api Class is a fancy es6 wrapper class for axios.
 *
 * @param {import("axios").AxiosRequestConfig} config - axios Request Config.
 * @link [AxiosRequestConfig](https://github.com/axios/axios#request-config)
 */

/* Helpers */
import { getSubdomain } from "../helpers/common.helper";

export class Api {
  private token: string;
  public instance: AxiosInstance;
  public instanceTokenless: AxiosInstance;
  /**
   * Creates an instance of api.
   * @param {import("axios").AxiosRequestConfig} conf
   */
  public constructor() {//(conf: AxiosRequestConfig) {
    //super();//(conf);
    this.instance = axios.create(ApiConfig);
    this.instanceTokenless = axios.create(ApiConfig);
    this.token = "";
    this.getToken = this.getToken.bind(this);
    this.setToken = this.setToken.bind(this);
    this.getUri = this.getUri.bind(this);
    this.request = this.request.bind(this);
    this.get = this.get.bind(this);
    this.options = this.options.bind(this);
    this.delete = this.delete.bind(this);
    this.head = this.head.bind(this);
    this.post = this.post.bind(this);
    this.put = this.put.bind(this);
    this.patch = this.patch.bind(this);
    this.success = this.success.bind(this);
    this.successOrder = this.successOrder.bind(this);
    this.error = this.error.bind(this);

    this.instance.interceptors.request.use((config) => {
      const url = window.location.hostname;
      const subDomain = getSubdomain(url);
      config.headers.common["token"] = this.getToken();

      config.headers.common["authorization"] = this.getToken();
      if (subDomain) {
        config.headers.common["platform"] = subDomain;
      }
      return config;
      // return {
      //     ...param,
      //     defaults: {
      //         headers: {
      //             ...param.headers,
      //             "Authorization": `${this.getToken()}`
      //         },
      //     }
      // }
    }, (error) => {
      // handling error
    });

    // this middleware is been called right before the response is get it by the method that triggers the request
    this.instance.interceptors.response.use((param) => ({
      ...param
    }), (error) => {
      if (!error.response) {
        return;
      }

      const {
        response: { status, data },
      } = error;

      if (status === 401) {
        localStorage.removeItem("userInfo");
        localStorage.removeItem("loginToken");
        localStorage.removeItem("i18nextLng");
        window.location.href = `/`;
      }
      if (data) return Promise.reject(data)
      return error.response;
    });

    this.instanceTokenless.interceptors.request.use((config) => {
      const url = window.location.hostname;
      const subDomain = getSubdomain(url);

      if (subDomain) {
        config.headers.common["platform"] = subDomain;
      }
      return config;
    });

    // this middleware is been called right before the response is get it by the method that triggers the request
    this.instanceTokenless.interceptors.response.use((param) => ({
      ...param
    }), (error) => {
      if (!error.response) return;

      const {
        response: { data },
      } = error;

      if (data) return Promise.reject(data)
      return error.response;
    });
  }
  /**
   * Gets Token.
   *
   * @param jsonObject
   * @returns {FormData} formdata.
   * @memberof FormData
   */
  public getFormData<T>(jsonObject: T): FormData {
    const formData = new FormData();
    for (const key in jsonObject) {
      try {
        formData.append(key, <any>jsonObject[key]);
      } catch { }
    }
    return formData;
  }
  /**
   * Gets Token.
   *
   * @returns {string} token.
   * @memberof Api
   */
  public getToken(): string {
    return `${this.token}`;
  }
  /**
   * Sets Token.
   *
   * @param {string} token - token.
   * @memberof Api
   */
  public setToken(token: string): void {
    this.token = token;
  }

  /**
   * Get Uri
   *
   * @param {import("axios").AxiosRequestConfig} [config]
   * @returns {string}
   * @memberof Api
   */
  public getUri(config?: AxiosRequestConfig): string {
    return this.instance.getUri(config);
  }
  /**
   * Generic request.
   *
   * @access public
   * @template T - `TYPE`: expected object.
   * @template R - `RESPONSE`: expected object inside a axios response format.
   * @param {import("axios").AxiosRequestConfig} [config] - axios request configuration.
   * @returns {Promise<R>} - HTTP axios response payload.
   * @memberof Api
   *
   * @example
   * api.request({
   *   method: "GET|POST|DELETE|PUT|PATCH"
   *   baseUrl: "http://www.domain.com",
   *   url: "/api/v1/users",
   *   headers: {
   *     "Content-Type": "application/json"
   *  }
   * }).then((response: AxiosResponse<User>) => response.data)
   *
   */
  public request<T, R = AxiosResponse<T>>(
    config: AxiosRequestConfig
  ): Promise<R> {
    return this.instance.request(config);
  }
  /**
   * HTTP GET method, used to fetch data `statusCode`: 200.
   *
   * @access public
   * @template T - `TYPE`: expected object.
   * @template R - `RESPONSE`: expected object inside a axios response format.
   * @param {string} url - endpoint you want to reach.
   * @param {import("axios").AxiosRequestConfig} [config] - axios request configuration.
   * @returns {Promise<R>} HTTP `axios` response payload.
   * @memberof Api
   */
  public get<T, R = AxiosResponse<T>>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<R> {
    return this.instance.get(url, config);
  }
  /**
   * HTTP OPTIONS method.
   *
   * @access public
   * @template T - `TYPE`: expected object.
   * @template R - `RESPONSE`: expected object inside a axios response format.
   * @param {string} url - endpoint you want to reach.
   * @param {import("axios").AxiosRequestConfig} [config] - axios request configuration.
   * @returns {Promise<R>} HTTP `axios` response payload.
   * @memberof Api
   */
  public options<T, R = AxiosResponse<T>>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<R> {
    return this.instance.options(url, config);
  }
  /**
   * HTTP DELETE method, `statusCode`: 204 No Content.
   *
   * @access public
   * @template T - `TYPE`: expected object.
   * @template R - `RESPONSE`: expected object inside a axios response format.
   * @param {string} url - endpoint you want to reach.
   * @param {import("axios").AxiosRequestConfig} [config] - axios request configuration.
   * @returns {Promise<R>} - HTTP [axios] response payload.
   * @memberof Api
   */
  public delete<T, R = AxiosResponse<T>>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<R> {
    return this.instance.delete(url, config);
  }
  /**
   * HTTP HEAD method.
   *
   * @access public
   * @template T - `TYPE`: expected object.
   * @template R - `RESPONSE`: expected object inside a axios response format.
   * @param {string} url - endpoint you want to reach.
   * @param {import("axios").AxiosRequestConfig} [config] - axios request configuration.
   * @returns {Promise<R>} - HTTP [axios] response payload.
   * @memberof Api
   */
  public head<T, R = AxiosResponse<T>>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<R> {
    return this.instance.head(url, config);
  }
  /**
   * HTTP POST method `statusCode`: 201 Created.
   *
   * @access public
   * @template T - `TYPE`: expected object.
   * @template B - `BODY`: body request object.
   * @template R - `RESPONSE`: expected object inside a axios response format.
   * @param {string} url - endpoint you want to reach.
   * @param {B} data - payload to be send as the `request body`,
   * @param {import("axios").AxiosRequestConfig} [config] - axios request configuration.
   * @returns {Promise<R>} - HTTP [axios] response payload.
   * @memberof Api
   */
  public async post<T, B, R = AxiosResponse<T>>(
    url: string,
    data?: B,
    config?: AxiosRequestConfig
  ): Promise<R> {
    return await this.instance.post(url, data, config);
  }
  /**
   * HTTP PUT method.
   *
   * @access public
   * @template T - `TYPE`: expected object.
   * @template B - `BODY`: body request object.
   * @template R - `RESPONSE`: expected object inside a axios response format.
   * @param {string} url - endpoint you want to reach.
   * @param {B} data - payload to be send as the `request body`,
   * @param {import("axios").AxiosRequestConfig} [config] - axios request configuration.
   * @returns {Promise<R>} - HTTP [axios] response payload.
   * @memberof Api
   */
  public put<T, B, R = AxiosResponse<T>>(
    url: string,
    data?: B,
    config?: AxiosRequestConfig
  ): Promise<R> {
    return this.instance.put(url, data, config);
  }
  /**
   * HTTP PATCH method.
   *
   * @access public
   * @template T - `TYPE`: expected object.
   * @template B - `BODY`: body request object.
   * @template R - `RESPONSE`: expected object inside a axios response format.
   * @param {string} url - endpoint you want to reach.
   * @param {B} data - payload to be send as the `request body`,
   * @param {import("axios").AxiosRequestConfig} [config] - axios request configuration.
   * @returns {Promise<R>} - HTTP [axios] response payload.
   * @memberof Api
   */
  public patch<T, B, R = AxiosResponse<T>>(
    url: string,
    data?: B,
    config?: AxiosRequestConfig
  ): Promise<R> {
    return this.instance.patch(url, data, config);
  }
  /**
   *
   * @template T - type.
   * @param {import("axios").AxiosResponse<T>} response - axios response.
   * @returns {T} - expected object.
   * @memberof Api
   */
  public success<T>(response: AxiosResponse<T>): T {
    return response?.data;
  }
  public successOrder<T>(response: AxiosResponse<any>): any {
    return response;
  }
  /**
   *
   *
   * @template T type.
   * @param {AxiosError<T>} error
   * @memberof Api
   */
  public error<T>(error: AxiosError<T>): void {
    throw error;
  }
  /**
   * HTTP GET with non token method, used to fetch data `statusCode`: 200.
   *
   * @access public
   * @template T - `TYPE`: expected object.
   * @template R - `RESPONSE`: expected object inside a axios response format.
   * @param {string} url - endpoint you want to reach.
   * @param {import("axios").AxiosRequestConfig} [config] - axios request configuration.
   * @returns {Promise<R>} HTTP `axios` response payload.
   * @memberof Api
   */
   public getWithNonToken<T, R = AxiosResponse<T>>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<R> {
    return this.instanceTokenless.get(url, config);
  }
}